import * as React from "react";
import { Static } from "@bizform/type/decorator/Static.decorator";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { Chips, ChipsProps, ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = ChipsProps & {
};

type WrappedCompState = {
    // attribute
    name?: string;
    tabindex?: number;
    placeholder?: string;
    value?: string[];
    max?: number;
    disabled?: boolean;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    autoAddOnBlur?: boolean;
    displayFlexible?: boolean;
    listItemStyle?: object;
    tooltipAttribute?: TooltipOptions & {text?: string};

    // event
    onChange?(e: ComponentEventType): void;
    itemTemplate?(item: any): JSX.Element | undefined;
    onAdd?(e: ComponentEventType): void;
    onRemove?(e: ComponentEventType): void;
};

type WrappedFormState = {
    onChange?: string;
    onAdd?: string;
    onRemove?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * BfChips component element
 *
 * @since 0.0.1
 * @author Byungmin Choi
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Array<string>>>()
export class BfChips extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_Jcc935dX2ZoFWW32OdSENk8tfzrSZ4K",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.chips.label,
        description: I18n.componentElement.chips.description,
        icon: "",
        dataType: [DataType.STRING, DataType.NUMBER, DataType.INTEGER],
        onlyArray: true,
        pathToLimitDataTypeArray: "max",
        property: null
    };

    focus(): void {
        if (this._chips && this._chips.inputElement) {
            this._chips.inputElement.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            newState.wrappedCompState.max = !newState.wrappedCompState.max ? 1 : newState.wrappedCompState.max;
            newState.wrappedCompState.max *= 1;
            // APPDEV-166: reversed
            newState.wrappedCompState.displayFlexible = newState.wrappedCompState.displayFlexible ? false : true;
            if (!newState.wrappedCompState.displayFlexible) {
                newState.wrappedCompState.listItemStyle = {
                    margin: "1px 0",
                    ...newState.wrappedCompState.listItemStyle
                };
            }
            if (newState.wrappedCompState.value) {
                if (newState.wrappedCompState.value.length > newState.wrappedCompState.max) {
                    newState.wrappedCompState.value = newState.wrappedCompState.value.slice(0, newState.wrappedCompState.max);
                    // if value length > max, it is impossible to adjust displayFlexible mode
                    this.setState(newState, this._callbackSetState);
                }
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Array<string>): void {
    }

    emitFormState(value: string): void {
    }

    emitSubscriptionReject(): void {
    }

    subscription(value: Array<string>): void {
        let newState;
        if (ComponentHelper.isNull(value)) {
            newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: []
                }
            };
            this.setState(newState);
        } else if (Array.isArray(value)) {
                newState = {
                    wrappedCompState: {
                        ...this.state.wrappedCompState,
                        value: ObjectHelper.cloneDeep(value)
                    }
                };
                this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfChips)
    static init(): ComponentElement<Array<string>> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _chips: any;
    private _placeholderTemp: string;
    private _placeholderString: string;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: []
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._onAddEvent = this._onAddEvent.bind(this);
        this._onRemoveEvent = this._onRemoveEvent.bind(this);
        this._setRef = this._setRef.bind(this);
        this._placeholder = this._placeholder.bind(this);
    }

    private _onChangeEvent(e?): void {
        // change state and then emit
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: [...e.value]
                }
            };
            // setState is an async function
            this.setState(newState, this._onChangeEventCallback);
        }
    }

    private _onAddEvent(e: ComponentEventType): void {
    }

    private _onRemoveEvent(e): void {
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    private _onChangeEventCallback() {
        this._callbackSetState();
    }

    componentDidMount(): void {
        if (this.state.wrappedCompState.value.length === this.state.wrappedCompState.max) {
            this._placeholderTemp = this.state.wrappedCompState.placeholder;

            if (this._chips.inputElement) {
                this._chips.inputElement.setAttribute("readonly", "true");
                this._chips.inputElement.placeholder = "";
            }
        }
    }

    componentDidUpdate(prevProps, prevState): void {
        if (this._chips && this._chips.inputElement && prevState.wrappedCompState.value.length === prevState.wrappedCompState.max) {
            this._chips.inputElement.removeAttribute("readonly");
            this._chips.inputElement.placeholder = this._placeholderTemp;
        }

        if (this.state.wrappedCompState.value.length >= this.state.wrappedCompState.max) {
            this._placeholderTemp = this.state.wrappedCompState.placeholder;

            if (this._chips && this._chips.inputElement) {
                this._chips.inputElement.setAttribute("readonly", "true");
                this._chips.inputElement.placeholder = "";
            }
        }

        this._placeholder();
        if (this._chips) {
            this._makeListStyle();
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._chips.inputElement, this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        this._chips = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        return (
            <Chips
                {...newProps}
                key={this.elementId}
                id={this.elementId}
                ref={this._setRef}
                accessibility={this.accessibility}
            />
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };
        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };
        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };
        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className= {`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{`${this.state.wrappedCompState.value ? this.state.wrappedCompState.value : ""}`}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._chips = ref;
            this._placeholder();
            this._makeListStyle();
            this.readyComponent();
        }
    }

    private _placeholder() {
        if (this._chips && this._chips.inputElement ) {
            if (!this.state.wrappedCompState.displayFlexible) {
                this._chips.inputElement.style.width = "";
                this._chips.inputElement.classList.add("w-100");
            } else if (this._placeholderString !== this.state.wrappedCompState.placeholder) {
                this._placeholderString = this.props.placeholder;
                ComponentHelper.setInputSize(this._chips.inputElement, this.state.wrappedCompState.placeholder, 100);
            }
        }
    }

    private _makeListStyle(newProps = {...this.state.wrappedCompState}) {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (this._chips && this._chips.element) {
                let uiTag = this._chips.element.querySelector("ul");
                uiTag.setAttribute("style", "");
                if (newProps.value.length === 0) {
                    if (newProps.displayFlexible === false) {
                        uiTag.style["display"] = "none";
                    }
                } else {
                    uiTag.style["display"] = "inline-block";
                }
                for (let item in (newProps as any).listStyle) {
                    uiTag.style[item] = (newProps as any).listStyle[item];
                }
                (newProps as any).listStyle = null;
            }
        }
    }
}